<script>
import Common from '../../../common/Common.vue'
import Endpoints from '../../../common/Endpoints.vue'
import Services from '../../../common/Services.vue'
import moment from 'moment-timezone'
import { applySessionScope } from "../../../utils/scope"

const PAGE_SIZE = 10

const ALERTPORT_EVENT_ICON_BY_TYPE = {
    SOS_ALERT: 'fa fa-bell',
    INCIDENT: 'fa fa-exclamation-circle',
    CRASH: 'fa fa-car-crash',
    LOWVOLTAGE: 'fa fa-battery-quarter',
    CANCEL_PATROL: 'fa fa-ban',
    FAILURE_PATROL: 'fa fa-exclamation-triangle',
    TIME_ENTRY: 'fa fa-clock',
    CLOCK_IN: 'fa fa-sign-in-alt',
    CLOCK_OUT: 'fa fa-sign-out-alt',
    BREAK_START: 'fa fa-coffee',
    BREAK_END: 'fa fa-mug-hot',
}

const ALERTPORT_EVENT_BADGE_BY_TYPE = {
    SOS_ALERT: 'badge bg-danger text-white me-1',
    INCIDENT: 'badge bg-orange text-white me-1',
    CRASH: 'badge bg-danger text-white me-1',
    LOWVOLTAGE: 'badge bg-warning text-dark me-1',
    CANCEL_PATROL: 'badge bg-indigo text-white me-1',
    FAILURE_PATROL: 'badge dark-orange me-1',
    TIME_ENTRY: 'badge bg-info text-white me-1',
    CLOCK_IN: 'badge bg-success text-white me-1',
    CLOCK_OUT: 'badge bg-secondary text-white me-1',
    BREAK_START: 'badge bg-warning text-dark me-1',
    BREAK_END: 'badge bg-warning text-dark me-1',
}

const TIME_ENTRY_LABEL = {
    CLOCK_IN: 'Entrada',
    CLOCK_OUT: 'Saída',
    BREAK_START: 'Início do intervalo',
    BREAK_END: 'Fim do intervalo',
}

export default {
    ALERTPORT_EVENT_ICON_BY_TYPE,
    ALERTPORT_EVENT_BADGE_BY_TYPE,
    TIME_ENTRY_LABEL,
    init: async payload => {
        try {
            payload.domain = Endpoints.domain
            payload.user = await payload.$session.get('user')
            payload.isSuperAdminMaster = await Common.isSuperAdminMaster(payload)
            payload.role = await Common.getSubtype(payload)

            if (payload.isSuperAdminMaster) {
                payload.accounts = await Services.getAccounts(payload)
            } else if (payload.role === 'ADMIN') {
                payload.clients = await Services.getClients(payload)
                payload.filters.account = await Common.getAccountId(payload)
            } else if (payload.role === 'MANAGER') {
                payload.clients = await Services.getClients(payload)
                payload.filters.account = await Common.getAccountId(payload)
                payload.filters.client = await Common.getClientId(payload)
            } else if (payload.role === 'OPERATOR') {
                payload.sites = await Services.getSites(payload)
                payload.filters.account = await Common.getAccountId(payload)
                payload.filters.client = await Common.getClientId(payload)
                payload.filters.sites = await Common.getSiteGroupId(payload)
            }

            try {
                payload.attendancesTypes = await Services.getAttendancesOptionsTypes(payload)
            } catch {
                payload.attendancesTypes = []
            }
        } catch (_error) {
            /* init error */
        }
    },
    methods: {
        async loadAccounts() {
            try {
                this.accounts = await Services.getAccounts(this)
            } catch {
                Common.show(this, 'bottom-right', 'danger', 'str.error_loading_accounts')
            }
        },
        async loadClients() {
            try {
                const account = this.filters.account || (await Common.getAccountId(this))
                if (!account) {
                    this.clients = []
                    return
                }
                this.clients = await Services.getClientsByAccount(this, account)
            } catch {
                Common.show(this, 'bottom-right', 'danger', 'str.error_loading_clients')
            }
        },
        async loadSites() {
            try {
                if (this.filters.client) {
                    this.sites = await Services.getSitesByClient(this, this.filters.client)
                    return
                }
                const account = this.filters.account || (await Common.getAccountId(this))
                if (account) {
                    this.sites = await Services.getSitesByAccount(this, account)
                    return
                }
                this.sites = []
            } catch {
                Common.show(this, 'bottom-right', 'danger', 'str.error_loading_sites')
            }
        },
        getAlertportQueryFilters() {
            const filters = {
                status: 'ACTIVE',
                sources: ['ALERTPORT'],
                useExactDateRange: true,
                startDate: moment().subtract(7, 'days').format(),
                endDate: moment().format(),
            }
            if (this.filters?.account) filters.account = this.filters.account
            if (this.filters?.client) filters.client = this.filters.client
            if (this.filters?.site) filters.site = this.filters.site
            return filters
        },
        async loadAlertportEvents(page = 1, { isFirstPage = true } = {}) {
            if (this.isSuperAdminMaster && !this.filters.account) {
                this.items = []
                this.events = []
                this.selectedEvent = null
                this.totalEventsCount = 0
                return
            }

            if (isFirstPage) {
                this.isLoadingEvents = true
                this.monitorPage = 1
                this.allEventsLoaded = false
                this.items = []
                this.events = []
            }

            try {
                const filters = {
                    ...this.getAlertportQueryFilters(),
                    limit: PAGE_SIZE,
                    skip: page,
                }
                const { results, totalCount } = await Services.getPatrolActionsPaginated(this, filters)
                const formatted = this.formatAlertportPatrolActions(results || [])

                if (isFirstPage) {
                    this.items = results || []
                    this.events = formatted
                } else {
                    this.items = [...this.items, ...(results || [])]
                    this.events = [...this.events, ...formatted]
                }

                this.totalEventsCount = totalCount != null ? totalCount : this.events.length
                this.monitorPage = page
                if ((results || []).length < PAGE_SIZE) this.allEventsLoaded = true

                if (this.selectedEvent) {
                    const refreshed = this.events.find(event => String(event?._id) === String(this.selectedEvent?._id))
                    if (refreshed) {
                        this.selectedEvent = JSON.parse(JSON.stringify(refreshed))
                    }
                }
            } catch (_error) {
                if (isFirstPage) {
                    this.items = []
                    this.events = []
                    this.totalEventsCount = 0
                }
            } finally {
                if (isFirstPage) this.isLoadingEvents = false
            }
        },
        async loadMoreAlertportEvents() {
            if (this.isLoadingMoreEvents || this.allEventsLoaded) return
            if (this.isSuperAdminMaster && !this.filters.account) return
            this.isLoadingMoreEvents = true
            try {
                await this.loadAlertportEvents(this.monitorPage + 1, { isFirstPage: false })
            } finally {
                this.isLoadingMoreEvents = false
            }
        },
        async loadAlertportTimeEntries() {
            if (this.isSuperAdminMaster && !this.filters.account) {
                this.timeEntries = []
                return
            }
            this.isLoadingTimeEntries = true
            try {
                const filters = {
                    status: 'ACTIVE',
                    sources: ['ALERTPORT'],
                    startDate: moment().subtract(7, 'days').format(),
                    endDate: moment().format(),
                    limit: 50,
                    skip: 1,
                }
                if (this.filters?.account) filters.account = this.filters.account
                if (this.filters?.client) filters.client = this.filters.client
                if (this.filters?.site) filters.site = this.filters.site
                this.timeEntries = await Services.getTimeEntries(this, filters)
            } catch {
                this.timeEntries = []
            } finally {
                this.isLoadingTimeEntries = false
            }
        },
        async refreshAlertportData() {
            await Promise.all([this.loadAlertportEvents(1, { isFirstPage: true }), this.loadAlertportTimeEntries()])
        },
        async filter() {
            if (!applySessionScope(this, this.filters)) return

            await this.refreshAlertportData()
            if (typeof this.setupAlertRealtimeListeners === 'function') this.setupAlertRealtimeListeners()
        },
        async changeAccount() {
            this.filters.client = ''
            this.filters.site = ''
            this.selectedEvent = null
            await this.loadClients()
            await this.loadSites()
            await this.filter()
            if (typeof this.loadCompanySettingsForRecording === 'function') await this.loadCompanySettingsForRecording()
        },
        async changeClient() {
            this.filters.site = ''
            this.selectedEvent = null
            await this.loadSites()
            await this.filter()
        },
        formatAlertportPatrolActions(list = []) {
            return list.map(item => {
                const {
                    _id,
                    status,
                    account,
                    client,
                    site,
                    event,
                    type,
                    date,
                    vigilant,
                    patrol,
                    soundURL,
                    photoURL,
                    signatureURL,
                    failurePatrolType,
                    incidents,
                    deviceInfo,
                    geolocation,
                    notes,
                    attendance,
                    source,
                } = item

                const vigilantFirst = vigilant?.firstName ? vigilant.firstName : ''
                const vigilantLast = vigilant?.lastName ? ' ' + vigilant.lastName : ''
                const deviceLabel = deviceInfo?.deviceId ? `Dispositivo ${deviceInfo.deviceId}` : 'Dispositivo AlertPort'
                const baseDescription = vigilantFirst
                    ? `${vigilantFirst}${vigilantLast}`.trim()
                    : deviceLabel

                const tag = ALERTPORT_EVENT_BADGE_BY_TYPE[type] || 'badge bg-secondary text-white me-1'
                const icon = ALERTPORT_EVENT_ICON_BY_TYPE[type] || 'fa fa-bell'

                let typeLabel = ''
                switch (type) {
                    case 'SOS_ALERT':
                        typeLabel = 'Alerta SOS'
                        break
                    case 'INCIDENT':
                        typeLabel = 'Incidente'
                        break
                    case 'CRASH':
                        typeLabel = 'Queda detectada'
                        break
                    case 'LOWVOLTAGE':
                        typeLabel = 'Bateria fraca'
                        break
                    case 'CANCEL_PATROL':
                        typeLabel = 'Ronda cancelada'
                        break
                    case 'FAILURE_PATROL':
                        typeLabel = 'Falha de ronda'
                        break
                    default:
                        typeLabel = type || 'Evento'
                }

                const dateTimezone = this.parseEventDateInAccountTimezone(date)
                const formattedDate = dateTimezone.isValid() ? dateTimezone.format('DD/MM/YYYY') : ''
                const formattedTime = dateTimezone.isValid() ? dateTimezone.format('HH:mm') : ''

                const description = `${baseDescription} ${typeLabel} em ${formattedDate} - ${formattedTime}`.trim()

                return {
                    _id,
                    status,
                    account,
                    client,
                    site,
                    event,
                    type,
                    date,
                    vigilant,
                    patrol,
                    soundURL,
                    photoURL,
                    signatureURL,
                    failurePatrolType,
                    incidents: incidents || [],
                    deviceInfo: deviceInfo || {},
                    geolocation: geolocation || {},
                    notes: notes || '',
                    attendance,
                    source: source || 'ALERTPORT',
                    title: type,
                    tag,
                    icon,
                    failureText: typeLabel,
                    description,
                    formattedDate,
                    formattedTime,
                }
            })
        },
        getMonitorTimezone() {
            return Common.resolveTimezoneId(this.user?.accountTimezone || this.user?.account?.timezone)
        },
        parseEventDateInAccountTimezone(date) {
            const timezone = this.getMonitorTimezone()
            if (!date) return moment.invalid()
            const hasTimezoneInfo = typeof date === 'string' && /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(date.trim())
            const parsedDate = hasTimezoneInfo ? moment(date) : moment.utc(date)
            if (!parsedDate.isValid()) return moment.invalid()
            return parsedDate.tz(timezone)
        },
        formatDate(date) {
            if (!date) return '-'
            const dateTimezone = this.parseEventDateInAccountTimezone(date)
            if (!dateTimezone.isValid()) return '-'
            return `${dateTimezone.format('DD/MM/YYYY')} - ${dateTimezone.format('HH:mm')}`
        },
        formatTimeEntryEventType(eventType) {
            return TIME_ENTRY_LABEL[eventType] || eventType || '-'
        },
        handleSelectEvent(event) {
            this.selectedEvent = JSON.parse(JSON.stringify(event))
            this.attendanceEventEnabled = false
            this.removeRequiredField('attendanceOptions')
            this.loadEventAttendances(event?._id)
        },
        async loadEventAttendances(patrolActionId) {
            if (!patrolActionId) {
                this.attendances = []
                return
            }
            this.isLoadingAttendanceList = true
            try {
                this.attendances = await Services.getEventAttendances(this, { patrolAction: patrolActionId, status: 'ACTIVE' })
            } catch {
                this.attendances = []
            } finally {
                this.isLoadingAttendanceList = false
            }
        },
        checkRequiredField(field) {
            return this.errors.includes(field)
        },
        removeRequiredField(field) {
            this.errors = this.errors.filter(item => item !== field)
        },
        async getOperatorId() {
            return this.$session.get('user')?._id
        },
        async handleAttendanceEvent() {
            if (this.isLoadingAttendanceEventButton || this.attendanceEventEnabled) return
            const patrolActionId = this.selectedEvent?._id
            if (!patrolActionId) return

            this.attendanceEventEnabled = true
            this.isLoadingAttendanceEventButton = true

            const operatorId = await this.getOperatorId()
            const siteGroup = this.$session.get('user')?.siteGroup?._id
            const attendance = {
                isAttendance: true,
                openedDate: moment().utc().format(),
                operator: operatorId,
                status: 'IN_PROGRESS',
            }

            try {
                const result = await Services.attendanceEvent(this, patrolActionId, attendance, siteGroup)
                if (result && this.selectedEvent) {
                    this.selectedEvent.attendance = {
                        isAttendance: result?.attendance?.isAttendance,
                        openedDate: result?.attendance?.openedDate,
                        closedDate: result?.attendance?.closedDate,
                        operator: this.user,
                        status: result?.attendance?.status,
                    }
                }
            } catch {
                /* ignore */
            } finally {
                this.attendanceEventEnabled = false
                this.isLoadingAttendanceEventButton = false
                this.clearForm()
            }
        },
        async closeAttendance() {
            if (!this.selectedEvent?._id) return
            this.attendanceEventEnabled = true
            this.isLoadingAttendanceEventButton = true

            const operatorId = await this.getOperatorId()
            const siteGroup = this.$session.get('user')?.siteGroup?._id
            const openedDate = this.selectedEvent.attendance?.openedDate
            const attendance = {
                isAttendance: true,
                openedDate,
                closedDate: moment().utc().format(),
                operator: operatorId,
                status: 'CLOSED',
            }

            try {
                const result = await Services.attendanceEvent(this, this.selectedEvent._id, attendance, siteGroup)
                if (result && this.selectedEvent) {
                    this.selectedEvent.attendance = {
                        isAttendance: result?.attendance?.isAttendance,
                        openedDate,
                        closedDate: result?.attendance?.closedDate,
                        operator: this.user,
                        status: result?.attendance?.status,
                    }
                }
            } catch {
                /* ignore */
            } finally {
                this.attendanceEventEnabled = false
                this.isLoadingAttendanceEventButton = false
                this.clearForm()
            }
        },
        confirmCloseAttendance() {
            if (!this.attendances || this.attendances.length === 0) {
                Common.show(this, 'bottom-right', 'error', this.$t('str.monitor.register.message.require'))
                return
            }
            this.$swal({
                title: this.$t('str.are.you.sure'),
                text: this.$t('str.are.you.sure.close'),
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: this.$t('str.title.close.attendance'),
                cancelButtonText: this.$t('str.title.cancel'),
                confirmButtonClass: 'btn me-5px btn-danger',
                cancelButtonClass: 'btn btn-default',
            }).then(result => {
                if (result.isConfirmed && result.value) {
                    this.closeAttendance()
                }
            })
        },
        async sendAttendanceEvent() {
            if (this.isLoadingSendAttendanceButton) return
            this.isLoadingSendAttendanceButton = true

            const attendance = {
                account: this.selectedEvent?.account?._id,
                client: this.selectedEvent?.client?._id,
                site: this.selectedEvent?.site?._id,
                event: this.selectedEvent?.event?._id,
                type: this.attendance?.type,
                notes: this.attendance?.notes,
                patrolAction: this.selectedEvent?._id,
                createDate: moment().utc().format(),
                status: 'ACTIVE',
                siteGroup: this.$session.get('user')?.siteGroup?._id,
                operator: await this.getOperatorId(),
            }

            try {
                await Services.createAttendance(this, attendance)
                this.attendances = await Services.getEventAttendances(this, { patrolAction: this.selectedEvent?._id, status: 'ACTIVE' })
                this.clearForm()
                this.attendanceEventEnabled = false
            } catch {
                Common.show(this, 'bottom-right', 'danger', 'str.error_creating_attendance')
            } finally {
                this.isLoadingSendAttendanceButton = false
            }
        },
        async checkForm() {
            this.errors = []
            if (!this.attendance?.type || this.attendance?.type === '') {
                this.errors.push('attendanceOptions')
            }
            if (this.errors.length === 0) {
                await this.sendAttendanceEvent()
            }
        },
        clearForm() {
            this.attendance.type = ''
            this.attendance.notes = ''
        },
        normalizeIdCandidate(value) {
            if (value === null || value === undefined) return ''
            if (typeof value === 'string' || typeof value === 'number') {
                return String(value).trim()
            }
            if (typeof value === 'object') {
                return this.normalizeIdCandidate(value?._id || value?.id || '')
            }
            return ''
        },
        firstValidId(...candidates) {
            for (const c of candidates) {
                const resolved = this.normalizeIdCandidate(c)
                if (resolved) return resolved
            }
            return ''
        },
        decodeSessionTokenPayload() {
            const token = this.$session?.get?.('token')
            if (!token || typeof token !== 'string') return null
            const parts = token.split('.')
            if (parts.length < 2) return null
            try {
                const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
                const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
                return JSON.parse(window.atob(padded))
            } catch {
                return null
            }
        },
        getSocketUserId() {
            const tp = this.decodeSessionTokenPayload()
            return this.firstValidId(this.user?._id, this.user?.id, tp?.userId, tp?.sub, tp?._id)
        },
        getSocketAccountId() {
            // The selected filter wins so SUPER_ADMIN_MASTER routes calls/notifications
            // to the account they are currently monitoring, not their white-label company.
            if (this.filters?.account) return this.filters.account

            const tp = this.decodeSessionTokenPayload()
            const fromUser = this.firstValidId(
                this.user?.account,
                this.user?.accountId,
                this.user?.companyUser?.account,
                this.user?.companyUser?.accountId,
                this.user?.companyUser?.company?.account,
                this.user?.companyUser?.company,
                this.user?.company,
                tp?.account,
                tp?.accountId,
                tp?.company,
            )
            if (fromUser) return fromUser
            const su = this.$session?.get?.('user')
            return this.firstValidId(su?.account, su?.accountId, su?.companyUser?.account, su?.company)
        },
        getSocketRole() {
            const tp = this.decodeSessionTokenPayload()
            return String(this.user?.companyUser?.subtype || this.role || tp?.role || '').trim().toUpperCase()
        },
        getSocketDisplayName() {
            const u = this.user || this.$session?.get?.('user') || {}
            const full = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim()
            return u?.fullName || full || u?.username || u?.email || 'Central'
        },
    },
}
</script>
